import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Gear,
  Bell,
  Key,
  Check,
  Warning,
  ArrowsClockwise,
  CircleNotch,
  CurrencyCircleDollar,
  Plus,
  Trash,
  PencilSimple,
  Storefront,
  ShieldCheck,
  Eye,
  EyeSlash,
  Question,
  BookOpen,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tax system types
const TAX_TYPES = [
  { value: "usn_6", label: "УСН 6% (доходы)", rate: 6 },
  { value: "usn_15", label: "УСН 15% (доходы - расходы)", rate: 15 },
  { value: "patent", label: "Патент", rate: 0 },
  { value: "osno", label: "ОСНО", rate: 20 },
  { value: "other", label: "Другое", rate: 0 },
];

// Marketplace types
const MARKETPLACE_TYPES = [
  { value: "ozon", label: "Ozon", color: "bg-blue-500", fields: ["client_id", "api_key"] },
  { value: "wildberries", label: "Wildberries", color: "bg-purple-500", fields: ["wb_token"] },
  { value: "yandex", label: "Яндекс.Маркет", color: "bg-yellow-500", fields: ["yandex_token", "yandex_campaign_id"] },
];

export default function Settings() {
  const { token, user } = useAuth();
  
  // Settings state
  const [settings, setSettings] = useState({
    low_stock_threshold: 10,
    notification_enabled: true,
    tax_type: "usn_6",
    tax_rate: 6,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Marketplace connections state
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [savingConnection, setSavingConnection] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  
  // New connection form
  const [newConnection, setNewConnection] = useState({
    marketplace: "ozon",
    name: "",
    client_id: "",
    api_key: "",
    perf_client_id: "",
    perf_api_key: "",
    wb_token: "",
    yandex_token: "",
    yandex_campaign_id: "",
  });

  const authHeaders = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  // Fetch settings
  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/settings`, { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  // Save settings
  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/settings`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(settings)
      });
      if (response.ok) {
        toast.success("Настройки сохранены");
      } else {
        toast.error("Ошибка сохранения настроек");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Ошибка сохранения настроек");
    } finally {
      setSaving(false);
    }
  };

  // Fetch marketplace connections
  const fetchConnections = async () => {
    try {
      const response = await fetch(`${API}/marketplaces`, { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setConnections(data);
      }
    } catch (error) {
      console.error("Error fetching connections:", error);
    } finally {
      setLoadingConnections(false);
    }
  };

  // Add/Update marketplace connection
  const saveConnection = async () => {
    setSavingConnection(true);
    try {
      const method = selectedConnection ? 'PUT' : 'POST';
      const url = selectedConnection 
        ? `${API}/marketplaces/${selectedConnection.id}`
        : `${API}/marketplaces`;

      const response = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify(newConnection)
      });

      if (response.ok) {
        toast.success(selectedConnection ? "Подключение обновлено" : "Подключение добавлено");
        setShowAddDialog(false);
        resetConnectionForm();
        fetchConnections();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Ошибка сохранения");
      }
    } catch (error) {
      console.error("Error saving connection:", error);
      toast.error("Ошибка сохранения подключения");
    } finally {
      setSavingConnection(false);
    }
  };

  // Delete marketplace connection
  const deleteConnection = async () => {
    if (!selectedConnection) return;
    
    try {
      const response = await fetch(`${API}/marketplaces/${selectedConnection.id}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        toast.success("Подключение удалено");
        setShowDeleteDialog(false);
        setSelectedConnection(null);
        fetchConnections();
      } else {
        toast.error("Ошибка удаления");
      }
    } catch (error) {
      console.error("Error deleting connection:", error);
      toast.error("Ошибка удаления подключения");
    }
  };

  // Test connection
  const testConnection = async (connectionId) => {
    try {
      const response = await fetch(`${API}/marketplaces/${connectionId}/test`, {
        method: 'POST',
        headers: authHeaders
      });
      const data = await response.json();
      
      if (data.status === 'ok') {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error("Ошибка проверки подключения");
    }
  };

  // Reset connection form
  const resetConnectionForm = () => {
    setNewConnection({
      marketplace: "ozon",
      name: "",
      client_id: "",
      api_key: "",
      perf_client_id: "",
      perf_api_key: "",
      wb_token: "",
      yandex_token: "",
      yandex_campaign_id: "",
    });
    setSelectedConnection(null);
  };

  // Edit connection
  const editConnection = (conn) => {
    setSelectedConnection(conn);
    // Pre-fill with masked values to show user what's stored
    const masked = conn.masked_credentials || {};
    setNewConnection({
      marketplace: conn.marketplace,
      name: conn.name,
      // Show masked values as placeholders - user can overwrite or leave empty to keep current
      client_id: "",
      api_key: "",
      perf_client_id: "",
      perf_api_key: "",
      wb_token: "",
      yandex_token: "",
      yandex_campaign_id: "",
      // Store masked values for display
      _masked: masked
    });
    setShowAddDialog(true);
  };

  useEffect(() => {
    fetchSettings();
    fetchConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMarketplaceInfo = (type) => MARKETPLACE_TYPES.find(m => m.value === type);

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h3 className="font-chivo font-bold text-2xl text-zinc-900">Настройки</h3>
        <p className="text-sm text-zinc-500 mt-1">
          Управление подключениями к маркетплейсам и параметрами системы
        </p>
      </div>

      {/* Marketplace Connections */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card data-testid="marketplaces-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-chivo text-lg flex items-center gap-2">
                <Storefront size={20} className="text-[#005BFF]" />
                Маркетплейсы
              </CardTitle>
              <CardDescription>
                Управление подключениями к торговым площадкам
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/settings/ozon-api-guide">
                <Button variant="outline" size="sm" className="gap-2" data-testid="ozon-guide-link">
                  <BookOpen size={16} />
                  Как получить API-ключи
                </Button>
              </Link>
              <Button onClick={() => { resetConnectionForm(); setShowAddDialog(true); }}>
                <Plus size={16} className="mr-2" />
                Добавить
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingConnections ? (
              <div className="flex items-center justify-center py-8">
                <CircleNotch size={24} className="animate-spin text-zinc-400" />
              </div>
            ) : connections.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <Storefront size={48} className="mx-auto mb-3 opacity-30" />
                <p>Нет подключенных маркетплейсов</p>
                <p className="text-sm">Добавьте первое подключение для начала работы</p>
              </div>
            ) : (
              <div className="space-y-3">
                {connections.map((conn) => {
                  const mpInfo = getMarketplaceInfo(conn.marketplace);
                  return (
                    <div
                      key={conn.id}
                      className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg border border-zinc-200"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg ${mpInfo?.color || 'bg-gray-500'} flex items-center justify-center text-white font-bold`}>
                          {mpInfo?.label?.charAt(0) || '?'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-zinc-900">{conn.name}</p>
                            <Badge variant={conn.status === 'active' ? 'default' : 'destructive'}>
                              {conn.status === 'active' ? 'Активно' : 'Ошибка'}
                            </Badge>
                          </div>
                          <p className="text-sm text-zinc-500">{mpInfo?.label}</p>
                          {conn.masked_credentials && (
                            <p className="text-xs text-zinc-400 font-mono mt-1">
                              {Object.entries(conn.masked_credentials).map(([key, val]) => (
                                <span key={key} className="mr-3">{key}: {val}</span>
                              ))}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => testConnection(conn.id)}
                          title="Проверить подключение"
                        >
                          <ArrowsClockwise size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => editConnection(conn)}
                          title="Редактировать"
                        >
                          <PencilSimple size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => { setSelectedConnection(conn); setShowDeleteDialog(true); }}
                          title="Удалить"
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Security note */}
            <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-100">
              <ShieldCheck size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-700">
                <p className="font-medium">Защита данных (ФЗ-152)</p>
                <p>API ключи хранятся в зашифрованном виде (AES-256). Мы не имеем доступа к вашим ключам в открытом виде.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <Separator />

      {/* Notifications settings */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card data-testid="notifications-card">
          <CardHeader>
            <CardTitle className="font-chivo text-lg flex items-center gap-2">
              <Bell size={20} className="text-amber-500" />
              Уведомления об остатках
            </CardTitle>
            <CardDescription>
              Настройка порога низких остатков
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="threshold">Порог низкого остатка (шт)</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="threshold"
                  type="number"
                  min="1"
                  max="1000"
                  value={settings.low_stock_threshold}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      low_stock_threshold: parseInt(e.target.value) || 10,
                    }))
                  }
                  className="w-32 font-mono"
                  data-testid="threshold-input"
                />
                <span className="text-sm text-zinc-500">
                  Товары с остатком ≤ {settings.low_stock_threshold} шт будут
                  отмечены как &ldquo;Мало на складе&rdquo;
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg">
              <div className="space-y-1">
                <Label htmlFor="notifications" className="font-medium">
                  Показывать уведомления
                </Label>
                <p className="text-sm text-zinc-500">
                  Отображать предупреждения о низких остатках на дашборде
                </p>
              </div>
              <Switch
                id="notifications"
                checked={settings.notification_enabled}
                onCheckedChange={(checked) =>
                  setSettings((prev) => ({
                    ...prev,
                    notification_enabled: checked,
                  }))
                }
                data-testid="notifications-switch"
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <Separator />

      {/* Tax settings */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <Card data-testid="tax-settings-card">
          <CardHeader>
            <CardTitle className="font-chivo text-lg flex items-center gap-2">
              <CurrencyCircleDollar size={20} className="text-emerald-500" />
              Налогообложение
            </CardTitle>
            <CardDescription>
              Настройка системы налогообложения для расчёта налогов
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="tax-type">Система налогообложения</Label>
              <Select
                value={settings.tax_type}
                onValueChange={(value) => {
                  const taxType = TAX_TYPES.find(t => t.value === value);
                  setSettings((prev) => ({
                    ...prev,
                    tax_type: value,
                    tax_rate: taxType?.rate || prev.tax_rate,
                  }));
                }}
              >
                <SelectTrigger className="w-72" data-testid="tax-type-select">
                  <SelectValue placeholder="Выберите систему" />
                </SelectTrigger>
                <SelectContent>
                  {TAX_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax-rate">Ставка налога (%)</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="tax-rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.tax_rate}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      tax_rate: parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="w-24 font-mono"
                  data-testid="tax-rate-input"
                />
                <span className="text-sm text-zinc-500">
                  Налог рассчитывается от суммы, оплаченной покупателем
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Save button */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex justify-end pt-4"
      >
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="btn-ozon"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <>
              <CircleNotch size={18} className="mr-2 animate-spin" />
              Сохранение...
            </>
          ) : (
            <>
              <Check size={18} className="mr-2" />
              Сохранить настройки
            </>
          )}
        </Button>
      </motion.div>

      {/* Add/Edit Marketplace Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedConnection ? 'Редактировать подключение' : 'Добавить маркетплейс'}
            </DialogTitle>
            <DialogDescription>
              {selectedConnection 
                ? 'Обновите данные подключения. Оставьте поля пустыми, чтобы сохранить текущие ключи.'
                : 'Введите данные для подключения к маркетплейсу'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Маркетплейс</Label>
              <Select
                value={newConnection.marketplace}
                onValueChange={(value) => setNewConnection(prev => ({ ...prev, marketplace: value }))}
                disabled={!!selectedConnection}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MARKETPLACE_TYPES.map((mp) => (
                    <SelectItem key={mp.value} value={mp.value}>
                      {mp.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Название магазина</Label>
              <Input
                placeholder="Мой магазин"
                value={newConnection.name}
                onChange={(e) => setNewConnection(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            {/* Ozon fields */}
            {newConnection.marketplace === 'ozon' && (
              <>
                <div className="space-y-1 pt-2">
                  <p className="text-sm font-medium text-zinc-700">Seller API</p>
                  <p className="text-xs text-zinc-500">Основной API для работы с товарами и заказами</p>
                </div>
                <div className="space-y-2">
                  <Label>Client ID</Label>
                  <Input
                    placeholder={selectedConnection ? (newConnection._masked?.client_id || "Текущий ключ сохранён") : "123456"}
                    value={newConnection.client_id}
                    onChange={(e) => setNewConnection(prev => ({ ...prev, client_id: e.target.value }))}
                  />
                  {selectedConnection && newConnection._masked?.client_id && (
                    <p className="text-xs text-zinc-500">Текущее значение: {newConnection._masked.client_id}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <div className="relative">
                    <Input
                      type={showPasswords.api_key ? "text" : "password"}
                      placeholder={selectedConnection ? (newConnection._masked?.api_key || "Текущий ключ сохранён") : "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"}
                      value={newConnection.api_key}
                      onChange={(e) => setNewConnection(prev => ({ ...prev, api_key: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                      onClick={() => setShowPasswords(prev => ({ ...prev, api_key: !prev.api_key }))}
                    >
                      {showPasswords.api_key ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {selectedConnection && newConnection._masked?.api_key && (
                    <p className="text-xs text-zinc-500">Текущее значение: {newConnection._masked.api_key}</p>
                  )}
                </div>
                
                <Separator className="my-2" />
                
                <div className="space-y-1">
                  <p className="text-sm font-medium text-zinc-700">Performance API</p>
                  <p className="text-xs text-zinc-500">Для работы с рекламным кабинетом (опционально)</p>
                </div>
                <div className="space-y-2">
                  <Label>Performance Client ID (Для работы с данными по рекламе)</Label>
                  <Input
                    placeholder={selectedConnection ? (newConnection._masked?.perf_client_id || "Текущий ключ сохранён") : "91212240"}
                    value={newConnection.perf_client_id}
                    onChange={(e) => setNewConnection(prev => ({ ...prev, perf_client_id: e.target.value }))}
                  />
                  {selectedConnection && newConnection._masked?.perf_client_id && (
                    <p className="text-xs text-zinc-500">Текущее значение: {newConnection._masked.perf_client_id}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Performance API Key</Label>
                  <div className="relative">
                    <Input
                      type={showPasswords.perf_api_key ? "text" : "password"}
                      placeholder={selectedConnection ? (newConnection._masked?.perf_client_secret || "Текущий ключ сохранён") : "Ключ из Performance API"}
                      value={newConnection.perf_api_key}
                      onChange={(e) => setNewConnection(prev => ({ ...prev, perf_api_key: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                      onClick={() => setShowPasswords(prev => ({ ...prev, perf_api_key: !prev.perf_api_key }))}
                    >
                      {showPasswords.perf_api_key ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {selectedConnection && newConnection._masked?.perf_client_secret && (
                    <p className="text-xs text-zinc-500">Текущее значение: {newConnection._masked.perf_client_secret}</p>
                  )}
                </div>
              </>
            )}

            {/* Wildberries fields */}
            {newConnection.marketplace === 'wildberries' && (
              <div className="space-y-2">
                <Label>API Токен</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.wb_token ? "text" : "password"}
                    placeholder="Токен из личного кабинета WB"
                    value={newConnection.wb_token}
                    onChange={(e) => setNewConnection(prev => ({ ...prev, wb_token: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    onClick={() => setShowPasswords(prev => ({ ...prev, wb_token: !prev.wb_token }))}
                  >
                    {showPasswords.wb_token ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-amber-600">
                  ⚠️ Интеграция с Wildberries в разработке
                </p>
              </div>
            )}

            {/* Yandex fields */}
            {newConnection.marketplace === 'yandex' && (
              <>
                <div className="space-y-2">
                  <Label>OAuth Токен</Label>
                  <div className="relative">
                    <Input
                      type={showPasswords.yandex_token ? "text" : "password"}
                      placeholder="OAuth токен"
                      value={newConnection.yandex_token}
                      onChange={(e) => setNewConnection(prev => ({ ...prev, yandex_token: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                      onClick={() => setShowPasswords(prev => ({ ...prev, yandex_token: !prev.yandex_token }))}
                    >
                      {showPasswords.yandex_token ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>ID Кампании</Label>
                  <Input
                    placeholder="12345678"
                    value={newConnection.yandex_campaign_id}
                    onChange={(e) => setNewConnection(prev => ({ ...prev, yandex_campaign_id: e.target.value }))}
                  />
                </div>
                <p className="text-xs text-amber-600">
                  ⚠️ Интеграция с Яндекс.Маркет в разработке
                </p>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); resetConnectionForm(); }}>
              Отмена
            </Button>
            <Button onClick={saveConnection} disabled={savingConnection || !newConnection.name}>
              {savingConnection ? (
                <CircleNotch size={16} className="animate-spin mr-2" />
              ) : null}
              {selectedConnection ? 'Сохранить' : 'Добавить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить подключение?</AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить. Подключение &ldquo;{selectedConnection?.name}&rdquo; будет удалено,
              но данные (товары, транзакции) останутся в системе.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={deleteConnection} className="bg-red-600 hover:bg-red-700">
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
